import { getAdminOrgId } from "./admin";
import { extractApiErrorMessage, getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function headers(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type WeeklyRuleDTO = {
  id?: string;
  weekday: number;
  /** Si défini, plage uniquement ce jour-là (pas de répétition hebdomadaire). */
  specific_date?: string | null;
  start_time: string;
  end_time: string;
  slot_duration_minutes?: number;
  timezone?: string;
};

export async function getMyBookingRules(): Promise<{ rules: WeeklyRuleDTO[] }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/me/rules`, { headers: headers(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Impossible de charger les disponibilités"));
  return res.json();
}

export async function putMyBookingRules(rules: Omit<WeeklyRuleDTO, "id">[]): Promise<void> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/me/rules`, {
    method: "PUT",
    headers: headers(orgId),
    body: JSON.stringify({ rules }),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Enregistrement impossible"));
}

export type OrgMemberBrief = { id: string; email: string; display_name: string };

export async function listOrgMembersForBooking(): Promise<{ items: OrgMemberBrief[] }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/org/members`, { headers: headers(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Impossible de charger les membres"));
  return res.json();
}

export async function createBookingLink(body: {
  title?: string | null;
  allowed_host_user_ids: string[];
}): Promise<{ id: string; url: string; token: string }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/links`, {
    method: "POST",
    headers: headers(orgId),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Création du lien impossible"));
  return res.json();
}

export type BookingLinkItem = {
  id: string;
  title: string | null;
  allowed_host_user_ids: string[];
  is_active: boolean;
  created_at: string | null;
  created_by_user_id: string;
  /** Présent si le jeton est stocké (copie possible). Sinon, appeler regenerateBookingLinkToken. */
  url?: string | null;
};

export async function listBookingLinks(): Promise<{ items: BookingLinkItem[] }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/links`, { headers: headers(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Impossible de charger les liens"));
  return res.json();
}

export async function deleteBookingLink(id: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/links/${id}`, {
    method: "DELETE",
    headers: headers(orgId),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Suppression impossible"));
}

/** Nouveau jeton : l’ancienne URL ne fonctionne plus. Retourne l’URL à copier. */
export async function regenerateBookingLinkToken(id: string): Promise<{ ok: boolean; url: string }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/links/${id}/regenerate-token`, {
    method: "POST",
    headers: headers(orgId),
  });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Régénération impossible"));
  return res.json();
}

export type AppointmentItem = {
  id: string;
  host_user_id: string;
  client_name: string;
  client_email: string;
  starts_at: string;
  ends_at: string;
  status: string;
  client_notes: string | null;
  public_link_id: string | null;
  created_at: string | null;
  host_display_name?: string;
};

export async function listMyAppointments(): Promise<{ items: AppointmentItem[] }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/appointments/me`, { headers: headers(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Impossible de charger les rendez-vous"));
  return res.json();
}

export async function listTeamAppointments(): Promise<{ items: AppointmentItem[] }> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/appointments/team`, { headers: headers(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Impossible de charger l’équipe"));
  return res.json();
}

export type TeamAvailabilityPreview = {
  items: { user_id: string; display_name: string; has_rules: boolean; upcoming_slot_count: number }[];
  from_date: string;
  to_date: string;
};

export async function getTeamAvailabilityPreview(): Promise<TeamAvailabilityPreview> {
  const orgId = await getAdminOrgId();
  const res = await fetch(`${API_BASE}/bookings/team/availability-preview?days=7`, { headers: headers(orgId) });
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Aperçu indisponible"));
  return res.json();
}

/** Public (sans auth) */
export async function getPublicBookingMeta(token: string): Promise<{
  title: string;
  organization_name: string;
  hosts: { id: string; display_name: string }[];
  /** Durée d’un créneau (minutes) ; absent sur anciennes API */
  slot_duration_minutes?: number | null;
  /** Si plusieurs durées : borne haute */
  slot_duration_minutes_max?: number | null;
}> {
  const res = await fetch(`${API_BASE}/public/booking/meta?t=${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Lien invalide"));
  return res.json();
}

export async function getPublicBookingSlots(
  token: string,
  hostUserId?: string,
  days: number = 45
): Promise<{
  hosts: { host_user_id: string; display_name: string; slots: { starts_at: string; ends_at: string }[] }[];
  from_date: string;
  to_date: string;
}> {
  const d = Math.min(45, Math.max(1, Math.floor(days)));
  const params = new URLSearchParams();
  params.set("t", token);
  params.set("days", String(d));
  if (hostUserId) params.set("host_user_id", hostUserId);
  const res = await fetch(`${API_BASE}/public/booking/slots?${params.toString()}`);
  if (!res.ok) throw new Error(await extractApiErrorMessage(res, "Créneaux indisponibles"));
  return res.json();
}

export async function publicBookAppointment(
  token: string,
  body: {
    host_user_id: string;
    starts_at: string;
    ends_at: string;
    client_name: string;
    client_email: string;
    client_notes?: string | null;
  }
): Promise<{ ok: boolean; confirmation_email_sent?: boolean; teams_invite_sent?: boolean }> {
  const res = await fetch(`${API_BASE}/public/booking/book?t=${encodeURIComponent(token)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const msg = await extractApiErrorMessage(res, "Réservation impossible");
    throw new Error(msg);
  }
  return res.json();
}
